import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Row, Col, Container, Tab, Nav, Card, Badge } from 'react-bootstrap'
import { useGetContentMetadataQuery } from '../../../services/contentApi'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useDatasetStats, type DatasetStats } from '../../../utils/datasetStats'
import { useAnnotationStats } from '../../../store/hooks'
import { ArrowLeft, Database, Tag, BarChart3, Grid3X3, FileText } from 'lucide-react'
import styled from 'styled-components'
import { PageWithTabs } from '../../common'
import DatasetOverviewTab from './components/DatasetOverviewTab'
import DatasetImagesTab from './components/DatasetImagesTab'
import DatasetSpecificContent from './components/DatasetSpecificContent'
import DatasetClassAnalysisTab from './components/DatasetClassAnalysisTab'

const BackButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: #ffffff;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

  svg {
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);

    svg {
      transform: translateX(-3px);
    }
  }

  &:active {
    transform: translateY(-2px) scale(0.98);
  }
`

const DatasetDetailView: React.FC = () => {
  const { projectId, datasetId } = useParams<{ projectId: string; datasetId: string }>()
  const navigate = useNavigate()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)

  const [activeTab, setActiveTab] = useState('overview')

  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: projectId || '',
    },
    { skip: !projectId }
  )

  // Get the specific dataset information
  const availableDatasets = useMemo(() => metadata?.metadata?.datasource || [], [metadata])
  const currentDataset = useMemo(() => {
    if (!datasetId) return null
    const decodedDatasetId = decodeURIComponent(datasetId)
    return availableDatasets.find((ds) => ds === decodedDatasetId) || decodedDatasetId
  }, [datasetId, availableDatasets])

  // Get statistics for the current dataset
  const datasetStats = useDatasetStats(
    projectId || '',
    project?.datasetid || '',
    currentDataset ? [currentDataset] : []
  )

  const currentDatasetStats = currentDataset ? datasetStats[currentDataset] : null

  // Debug logging

  const handleBackToAllDatasets = () => {
    navigate(`/datasets/${projectId}/all`)
  }

  if (!currentDataset) {
    return (
      <div className="text-center py-5">
        <Database size={64} className="text-muted mb-3" />
        <h3 className="text-white">Loading Dataset...</h3>
        <p className="text-muted">Please wait while we load the dataset information.</p>
        <BackButton onClick={handleBackToAllDatasets}>
          <ArrowLeft size={18} />
          Back to All Datasets
        </BackButton>
      </div>
    )
  }

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      icon: <BarChart3 size={20} />,
    },
    {
      key: 'images',
      label: 'Content',
      icon: <Grid3X3 size={20} />,
    },
    {
      key: 'class-analysis',
      label: 'Class Distribution',
      icon: <Tag size={20} />,
    },
    {
      key: 'content',
      label: 'Manage',
      icon: <FileText size={20} />,
    },
  ]

  return (
    <div>
      {/* Back Button */}
      <div className="mb-3">
        <BackButton onClick={handleBackToAllDatasets}>
          <ArrowLeft size={18} />
          Back to All Datasets
        </BackButton>
      </div>

      {/* Dataset Identification Header */}
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between">
          {/* Left side - Icon and basic info */}
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                color: 'white',
              }}
            >
              <Database size={24} />
            </div>
            <div>
              <h2
                className="mb-1"
                style={{ fontSize: '1.5rem', fontWeight: '600', color: '#ffffff' }}
              >
                {currentDataset.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </h2>
              <p className="mb-0" style={{ fontSize: '0.9rem', color: '#e0e0e0' }}>
                Dataset Details & Management
              </p>
            </div>
          </div>

          {/* Right side - Project info and badges */}
          {project && (
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex flex-column align-items-end">
                <Badge bg="secondary" className="px-2 py-1 mb-1">
                  {project.annotation_type
                    ?.replace(/_/g, ' ')
                    .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Classification'}
                </Badge>
                <span style={{ fontSize: '0.85rem', color: '#d0d0d0' }}>
                  Project: {project.name}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs Content */}
      <PageWithTabs
        tabs={tabItems}
        activeTab={activeTab}
        onTabSelect={(k) => setActiveTab(k || 'overview')}
        tabContent={
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')}>
            <Tab.Content>
              <Tab.Pane eventKey="overview">
                <DatasetOverviewTab
                  datasetSource={currentDataset}
                  stats={currentDatasetStats || undefined}
                  projectId={projectId || ''}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="images">
                <DatasetImagesTab
                  datasetSource={currentDataset}
                  projectId={projectId || ''}
                  project={project}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="class-analysis">
                <DatasetClassAnalysisTab
                  datasetSource={currentDataset}
                  projectId={projectId || ''}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="content">
                <DatasetSpecificContent datasetSource={currentDataset} />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        }
        children={null}
      />
    </div>
  )
}

export default DatasetDetailView
